import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  formatRequestedModulesLabel,
  formatRequiresInvoiceLabel,
  invoiceStatusLabel,
  licenseRequestStatusLabel,
} from "@/lib/commercialLicenseRequests";
import {
  scopeRangeLabelClients,
  scopeRangeLabelUsers,
} from "@/lib/commercialLicenseScope";
import {
  cfdiUseSelectLabel,
  paymentMethodSelectLabel,
} from "@/lib/commercialInvoiceCatalog";
import { licenseTypeLabel } from "@/lib/licenseTypes";
import { listMyLicenseRequests } from "@/lib/commercialLicenseRequestsApi";

function statusBadgeClass(status) {
  switch (status) {
    case "pending":
      return "bg-amber-50 text-amber-900 border-amber-200";
    case "in_review":
      return "bg-sky-50 text-sky-900 border-sky-200";
    case "approved":
      return "bg-emerald-50 text-emerald-900 border-emerald-200";
    case "rejected":
      return "bg-red-50 text-red-900 border-red-200";
    case "issued":
      return "bg-indigo-50 text-indigo-900 border-indigo-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function formatDateShort(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 10);
}

/** Historial de solicitudes de licencia del comprador. */
export default function LicenseRequestHistory({ enabled = true }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoadError("");
    try {
      const data = await listMyLicenseRequests();
      setRequests(data?.items || []);
    } catch {
      setLoadError("No pudimos cargar tu historial de solicitudes.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    refresh();
  }, [enabled, refresh]);

  if (!enabled) return null;

  return (
    <div className="pt-2 border-t border-slate-200 space-y-2" data-testid="license-request-history-block">
      <h5 className="text-xs font-semibold text-slate-800 m-0">Historial de solicitudes</h5>
      {loading ? (
        <p className="text-[11px] text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando…
        </p>
      ) : loadError ? (
        <p className="text-[11px] text-red-700">{loadError}</p>
      ) : requests.length === 0 ? (
        <p className="text-[11px] text-slate-500" data-testid="license-request-history-empty">
          Aún no has enviado solicitudes de licencia.
        </p>
      ) : (
        <div className="space-y-2" data-testid="license-request-history">
          {requests.map((row) => (
            <div
              key={row.id}
              className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-[11px] space-y-1"
              data-testid={`license-request-history-row-${row.status}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-slate-800">{formatDateShort(row.created_at)}</span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-sm border font-semibold ${statusBadgeClass(row.status)}`}
                >
                  {licenseRequestStatusLabel(row.status)}
                </span>
              </div>
              <div className="text-slate-700">
                <span className="font-medium">Tipo de licencia:</span>{" "}
                {licenseTypeLabel(row.requested_license_type)}
              </div>
              <div className="text-slate-700">
                <span className="font-medium">Módulos:</span>{" "}
                {formatRequestedModulesLabel(row.requested_modules) || "—"}
              </div>
              {(row.estimated_users > 0 || row.estimated_clients > 0) ? (
                <div className="text-slate-700">
                  <span className="font-medium">Alcance estimado:</span>{" "}
                  {row.estimated_users || "—"} usuarios
                  {row.estimated_users_range
                    ? ` (${scopeRangeLabelUsers(row.estimated_users_range)})`
                    : ""}
                  {" · "}
                  {row.estimated_clients || "—"} clientes
                  {row.estimated_clients_range
                    ? ` (${scopeRangeLabelClients(row.estimated_clients_range)})`
                    : ""}
                </div>
              ) : null}
              {row.commercial_review_required ? (
                <span className="inline-block text-[10px] px-2 py-0.5 rounded-sm border font-semibold bg-amber-50 text-amber-900 border-amber-200">
                  Requiere revisión comercial
                </span>
              ) : null}
              {row.special_quote_required ? (
                <span className="inline-block text-[10px] px-2 py-0.5 rounded-sm border font-semibold bg-violet-50 text-violet-900 border-violet-200 ml-1">
                  Cotización especial
                </span>
              ) : null}
              <div className="text-slate-700">
                <span className="font-medium">Factura:</span>{" "}
                {formatRequiresInvoiceLabel(row.requires_invoice)}
                {row.requires_invoice && row.invoice_status
                  ? ` (${invoiceStatusLabel(row.invoice_status)})`
                  : ""}
              </div>
              {row.requires_invoice ? (
                <div className="text-slate-600 text-[10px] space-y-0.5">
                  <div>
                    Domicilio fiscal: {row.invoice_fiscal_address || "No especificado"}
                  </div>
                  <div>
                    Uso CFDI:{" "}
                    {row.invoice_cfdi_use_label || cfdiUseSelectLabel(row.invoice_cfdi_use)}
                  </div>
                  <div>
                    Forma de pago:{" "}
                    {row.invoice_payment_method_label
                      || paymentMethodSelectLabel(row.invoice_payment_method)}
                  </div>
                </div>
              ) : null}
              {row.comments ? (
                <div className="text-slate-600">
                  <span className="font-medium">Comentarios:</span> {row.comments}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
